import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

const readinessSections = [
  {
    id: 'partner-summary',
    letter: 'A',
    title: 'Partner readiness summary',
    intro:
      'Internal readiness tracker for future relying parties, BOSA/FAS discussions, technical partners, and potential pilots. It is for preparation only and does not claim production readiness, official approval, or formal recognition.',
    items: [
      {
        area: 'Partner readiness summary',
        component: 'Purpose of the page',
        linkTo: '/internal',
        linkLabel: 'Open internal dashboard',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Use this page as an internal tracker, not as a public integration guide.',
      },
      {
        area: 'Partner readiness summary',
        component: 'Current maturity: internal lab / readiness preparation',
        linkTo: '/internal/digital-key-demo',
        linkLabel: 'Open digital key demo',
        status: 'Internal lab',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Keep the maturity label explicit and avoid official certification language.',
      },
      {
        area: 'Partner readiness summary',
        component: 'What exists today',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Open OIDC lab',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Continue using the lab flow, safe claims preview, and internal demo runner as the baseline evidence.',
      },
      {
        area: 'Partner readiness summary',
        component: 'What is still draft',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Open audit governance',
        status: 'Draft',
        strength: 'Medium',
        risk: 'Medium',
        nextAction: 'Convert the remaining partner assumptions into explicit internal notes and checklists.',
      },
      {
        area: 'Partner readiness summary',
        component: 'What requires partner/BOSA/FAS validation',
        linkTo: '/internal/fas-readiness',
        linkLabel: 'Open FAS readiness',
        status: 'Needs BOSA/FAS validation',
        strength: 'Placeholder',
        risk: 'High',
        nextAction: 'Confirm protocol, claim, and recognition assumptions with BOSA/FAS before any external discussion.',
      },
    ],
  },
  {
    id: 'relying-party-model',
    letter: 'B',
    title: 'Relying party model',
    intro:
      'The partner model is framed as an internal relying party / client configuration concept. These items stay lab-only and should be validated before any broader partner conversation.',
    items: [
      {
        area: 'Relying party model',
        component: 'Relying party profiles',
        linkTo: '/internal/relying-party-profiles',
        linkLabel: 'Open relying party profiles',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Keep profile data minimal and internal-only.',
      },
      {
        area: 'Relying party model',
        component: 'Internal relying party / client configuration concept',
        linkTo: '/internal/relying-party-profiles',
        linkLabel: 'Open configuration view',
        status: 'Draft',
        strength: 'Medium',
        risk: 'Medium',
        nextAction: 'Define what partner registration fields are actually needed and what stays deferred.',
      },
      {
        area: 'Relying party model',
        component: 'Redirect / callback simulation',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Open OIDC lab',
        status: 'Internal lab',
        strength: 'Strong',
        risk: 'Medium',
        nextAction: 'Keep callback handling internal and tied to the simulator only.',
      },
      {
        area: 'Relying party model',
        component: 'Requested claims concept',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Review requested claims',
        status: 'Draft',
        strength: 'Medium',
        risk: 'High',
        nextAction: 'Define claim requests as an allowlist and avoid over-collection.',
      },
      {
        area: 'Relying party model',
        component: 'Consent / approval flow concept',
        linkTo: '/internal/authenticator-preview',
        linkLabel: 'Open authenticator preview',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Keep approval decisions internal and limited to safe metadata.',
      },
    ],
  },
  {
    id: 'technical-flow-readiness',
    letter: 'C',
    title: 'Technical flow readiness',
    intro:
      'This section tracks the current end-to-end internal flow from selected identity and digital key account through request creation, approval, return, and safe claims preview.',
    items: [
      {
        area: 'Technical flow readiness',
        component: 'Identity selected',
        linkTo: '/internal/identities',
        linkLabel: 'Open identities',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Keep the selection tied to approved verification-derived identities only.',
      },
      {
        area: 'Technical flow readiness',
        component: 'Digital key account selected',
        linkTo: '/internal/digital-key-accounts',
        linkLabel: 'Open digital key accounts',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Preserve the safe account linking metadata and internal-only controls.',
      },
      {
        area: 'Technical flow readiness',
        component: 'Login request created',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Open OIDC lab',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Keep request IDs and simulated client metadata internal only.',
      },
      {
        area: 'Technical flow readiness',
        component: 'Authenticator review',
        linkTo: '/internal/authenticator-preview',
        linkLabel: 'Open authenticator preview',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Keep approve / deny actions limited to safe request metadata.',
      },
      {
        area: 'Technical flow readiness',
        component: 'Return / finalize flow',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Return to OIDC lab',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Continue using the handoff query parameters for internal navigation only.',
      },
      {
        area: 'Technical flow readiness',
        component: 'Safe claims preview',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Review safe claims',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Do not surface raw evidence, tokens, or regulated identifiers.',
      },
    ],
  },
  {
    id: 'api-boundaries',
    letter: 'D',
    title: 'API and route boundary readiness',
    intro:
      'The internal lab must remain isolated from normal workspace APIs. These items document the boundary conditions that keep the internal surfaces safe and platform-admin-only.',
    items: [
      {
        area: 'API and route boundary readiness',
        component: 'Strict internal API prefixes',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Open audit governance',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Keep internal routers mounted only under narrow internal prefixes.',
      },
      {
        area: 'API and route boundary readiness',
        component: 'Internal-only OIDC Lab endpoints',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Open OIDC lab',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Continue keeping the lab endpoints internal and metadata-only.',
      },
      {
        area: 'API and route boundary readiness',
        component: 'No public login endpoint yet',
        linkTo: '/internal/digital-key-architecture',
        linkLabel: 'Open digital key architecture',
        status: 'Not started',
        strength: 'Placeholder',
        risk: 'High',
        nextAction: 'Do not introduce a public login surface until the full protocol and governance model are defined.',
      },
      {
        area: 'API and route boundary readiness',
        component: 'No production OIDC / OAuth tokens',
        linkTo: '/internal/digital-key-architecture',
        linkLabel: 'Open digital key architecture',
        status: 'Out of scope for MVP',
        strength: 'Placeholder',
        risk: 'High',
        nextAction: 'Keep token issuance out of this internal readiness tracker.',
      },
      {
        area: 'API and route boundary readiness',
        component: 'No broad /api internal router mounting',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Preserve the separation between workspace APIs and internal admin APIs.',
      },
      {
        area: 'API and route boundary readiness',
        component: 'Separation between workspace APIs and internal lab APIs',
        linkTo: '/internal',
        linkLabel: 'Open internal dashboard',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Keep the customer workspace and internal portal distinct in both UI and routing.',
      },
    ],
  },
  {
    id: 'security-expectations',
    letter: 'E',
    title: 'Security expectations for partners',
    intro:
      'The items below are placeholders for the security expectations that a future partner discussion would need to validate. They are not production commitments.',
    items: [
      {
        area: 'Security expectations for partners',
        component: 'HTTPS requirement',
        linkTo: '/internal/fas-readiness',
        linkLabel: 'Open FAS readiness',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Treat HTTPS as a baseline requirement only.',
      },
      {
        area: 'Security expectations for partners',
        component: 'Redirect / callback validation placeholder',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Open OIDC lab',
        status: 'Draft',
        strength: 'Medium',
        risk: 'Medium',
        nextAction: 'Define exact callback rules and validation checks before any partner pilot.',
      },
      {
        area: 'Security expectations for partners',
        component: 'Client registration placeholder',
        linkTo: '/internal/relying-party-profiles',
        linkLabel: 'Open relying party profiles',
        status: 'Draft',
        strength: 'Medium',
        risk: 'Medium',
        nextAction: 'Document the minimum safe partner-registration fields and trust checks.',
      },
      {
        area: 'Security expectations for partners',
        component: 'Token / signature validation placeholder',
        linkTo: '/internal/digital-key-architecture',
        linkLabel: 'Open digital key architecture',
        status: 'Not started',
        strength: 'Placeholder',
        risk: 'High',
        nextAction: 'Keep cryptographic validation and trust modeling in the lab stage until requirements are confirmed.',
      },
      {
        area: 'Security expectations for partners',
        component: 'Audit logging expectations',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Preserve the safe event trail and reason capture model.',
      },
      {
        area: 'Security expectations for partners',
        component: 'Incident contact placeholder',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Not started',
        strength: 'Placeholder',
        risk: 'Medium',
        nextAction: 'Create a future partner incident contact and escalation record only if a pilot is approved.',
      },
      {
        area: 'Security expectations for partners',
        component: 'Rate limiting / abuse controls placeholder',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Audit governance',
        status: 'Not started',
        strength: 'Placeholder',
        risk: 'Medium',
        nextAction: 'Define expected abuse handling only after the future protocol scope is agreed.',
      },
      {
        area: 'Security expectations for partners',
        component: 'Environment separation placeholder',
        linkTo: '/internal',
        linkLabel: 'Open internal dashboard',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Keep internal lab, workspace, and any future partner environments separated.',
      },
    ],
  },
  {
    id: 'claims-minimization',
    letter: 'F',
    title: 'Claims and data minimization readiness',
    intro:
      'The partner flow must remain safe-claims only. These controls prevent raw evidence or regulated identifiers from leaking into partner-facing concepts.',
    items: [
      {
        area: 'Claims and data minimization readiness',
        component: 'Safe claims preview only',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Review safe claims',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Keep the lab output limited to the minimal safe claims set.',
      },
      {
        area: 'Claims and data minimization readiness',
        component: 'No RRN/BIS in current scope',
        linkTo: '/internal/regulated-identifiers',
        linkLabel: 'Open regulated identifiers',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Keep regulated identifiers outside the current partner readiness model.',
      },
      {
        area: 'Claims and data minimization readiness',
        component: 'No raw identity evidence to partners',
        linkTo: '/internal/bosa-evidence-pack',
        linkLabel: 'Open BOSA evidence pack',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Only share narrative evidence internally and never expose the underlying raw artifacts.',
      },
      {
        area: 'Claims and data minimization readiness',
        component: 'No document / selfie images',
        linkTo: '/internal/bosa-evidence-pack',
        linkLabel: 'Open BOSA evidence pack',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Keep all evidence packs text-only and metadata-only.',
      },
      {
        area: 'Claims and data minimization readiness',
        component: 'No OCR / MRZ raw data',
        linkTo: '/internal/bosa-evidence-pack',
        linkLabel: 'Open BOSA evidence pack',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Continue suppressing raw extraction payloads.',
      },
      {
        area: 'Claims and data minimization readiness',
        component: 'Claim minimization by use case',
        linkTo: '/internal/relying-party-profiles',
        linkLabel: 'Open relying party profiles',
        status: 'Draft',
        strength: 'Medium',
        risk: 'Medium',
        nextAction: 'Define per-use-case claim allowlists and document why each claim is needed.',
      },
      {
        area: 'Claims and data minimization readiness',
        component: 'Consent / approval concept',
        linkTo: '/internal/authenticator-preview',
        linkLabel: 'Open authenticator preview',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Keep consent and approval strictly internal and lab-only.',
      },
    ],
  },
  {
    id: 'sandbox-pilot-readiness',
    letter: 'G',
    title: 'Sandbox / pilot readiness',
    intro:
      'These items document the internal lab state and the work still needed before any partner pilot conversation could happen.',
    items: [
      {
        area: 'Sandbox / pilot readiness',
        component: 'Internal lab status',
        linkTo: '/internal/digital-key-demo',
        linkLabel: 'Open digital key demo',
        status: 'Internal lab',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Use the lab as the default internal demonstration path.',
      },
      {
        area: 'Sandbox / pilot readiness',
        component: 'Sandbox concept',
        linkTo: '/internal/fas-readiness',
        linkLabel: 'Open FAS readiness',
        status: 'Draft',
        strength: 'Medium',
        risk: 'Medium',
        nextAction: 'Define a safe sandbox boundary only if a pilot becomes realistic.',
      },
      {
        area: 'Sandbox / pilot readiness',
        component: 'Test relying party profile',
        linkTo: '/internal/relying-party-profiles',
        linkLabel: 'Open relying party profiles',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Keep test profile data minimal and internal-only.',
      },
      {
        area: 'Sandbox / pilot readiness',
        component: 'Test identity / account flow',
        linkTo: '/internal/identities',
        linkLabel: 'Open identities',
        status: 'Implemented',
        strength: 'Strong',
        risk: 'Low',
        nextAction: 'Continue using approved verification-derived identities only.',
      },
      {
        area: 'Sandbox / pilot readiness',
        component: 'Test callback flow',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Open OIDC lab',
        status: 'Internal lab',
        strength: 'Strong',
        risk: 'Medium',
        nextAction: 'Keep callback testing limited to the internal simulator.',
      },
      {
        area: 'Sandbox / pilot readiness',
        component: 'Pilot checklist placeholder',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Open audit governance',
        status: 'Not started',
        strength: 'Placeholder',
        risk: 'High',
        nextAction: 'Draft the safety, security, and operational checks that a pilot would require.',
      },
      {
        area: 'Sandbox / pilot readiness',
        component: 'Partner onboarding checklist placeholder',
        linkTo: '/internal/partner-technical-readiness',
        linkLabel: 'Current page',
        status: 'Not started',
        strength: 'Placeholder',
        risk: 'High',
        nextAction: 'Hold this until the protocol, security, and governance questions are answered.',
      },
    ],
  },
  {
    id: 'open-questions',
    letter: 'H',
    title: 'Technical open questions',
    intro:
      'The items below are the questions that still need to be answered by BOSA/FAS or by future partner discussions. They are intentionally unresolved today.',
    items: [
      {
        area: 'Technical open questions',
        component: 'Required protocol expectations from BOSA/FAS',
        linkTo: '/internal/fas-readiness',
        linkLabel: 'Open FAS readiness',
        status: 'Needs BOSA/FAS validation',
        strength: 'Placeholder',
        risk: 'High',
        nextAction: 'Confirm the expected protocol baseline before any partner-facing work.',
      },
      {
        area: 'Technical open questions',
        component: 'OIDC / SAML expectations',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Open OIDC lab',
        status: 'Needs partner validation',
        strength: 'Placeholder',
        risk: 'High',
        nextAction: 'Record whether the future path is OIDC, SAML, or another model.',
      },
      {
        area: 'Technical open questions',
        component: 'Client registration requirements',
        linkTo: '/internal/relying-party-profiles',
        linkLabel: 'Open relying party profiles',
        status: 'Needs partner validation',
        strength: 'Placeholder',
        risk: 'High',
        nextAction: 'Define registration, trust, and lifecycle expectations only after the partner model is confirmed.',
      },
      {
        area: 'Technical open questions',
        component: 'Redirect URI validation requirements',
        linkTo: '/internal/oidc-lab',
        linkLabel: 'Open OIDC lab',
        status: 'Needs partner validation',
        strength: 'Placeholder',
        risk: 'High',
        nextAction: 'Capture the exact redirect rules and error handling expected by the partner.',
      },
      {
        area: 'Technical open questions',
        component: 'Claim set expectations',
        linkTo: '/internal/relying-party-profiles',
        linkLabel: 'Open relying party profiles',
        status: 'Needs BOSA/FAS validation',
        strength: 'Placeholder',
        risk: 'High',
        nextAction: 'Confirm which claims are allowed and which must stay out of scope.',
      },
      {
        area: 'Technical open questions',
        component: 'Assurance / LoA mapping expectations',
        linkTo: '/internal/fas-readiness',
        linkLabel: 'Open FAS readiness',
        status: 'Needs BOSA/FAS validation',
        strength: 'Placeholder',
        risk: 'High',
        nextAction: 'Avoid naming an official LoA until BOSA/FAS guidance is explicit.',
      },
      {
        area: 'Technical open questions',
        component: 'Audit / security evidence expected from a partner',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Open audit governance',
        status: 'Draft',
        strength: 'Medium',
        risk: 'Medium',
        nextAction: 'Prepare a question list instead of assuming partner obligations.',
      },
      {
        area: 'Technical open questions',
        component: 'Whether a sandbox / test environment is available',
        linkTo: '/internal/digital-key-demo',
        linkLabel: 'Open digital key demo',
        status: 'Not started',
        strength: 'Placeholder',
        risk: 'Medium',
        nextAction: 'Record whether a partner sandbox exists before planning any pilot work.',
      },
      {
        area: 'Technical open questions',
        component: 'Whether partner pilots are useful before formal recognition',
        linkTo: '/internal/fas-readiness',
        linkLabel: 'Open FAS readiness',
        status: 'Draft',
        strength: 'Medium',
        risk: 'Medium',
        nextAction: 'Keep pilot usefulness separate from any formal recognition claim.',
      },
      {
        area: 'Technical open questions',
        component: 'Commercial / contractual onboarding expectations',
        linkTo: '/internal/audit-governance-pack',
        linkLabel: 'Open audit governance',
        status: 'Not started',
        strength: 'Placeholder',
        risk: 'Medium',
        nextAction: 'Document this only if partner onboarding becomes an approved path.',
      },
    ],
  },
];

const statusStyles = {
  Implemented: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  'Internal lab': 'border-sky-300 bg-sky-50 text-sky-800',
  Partial: 'border-amber-300 bg-amber-50 text-amber-800',
  Draft: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  'Needs validation': 'border-rose-300 bg-rose-50 text-rose-700',
  'Needs partner validation': 'border-rose-300 bg-rose-50 text-rose-700',
  'Needs BOSA/FAS validation': 'border-rose-300 bg-rose-50 text-rose-700',
  'Not started': 'border-zinc-200 bg-zinc-50 text-zinc-600',
  'Out of scope for MVP': 'border-zinc-200 bg-zinc-50 text-zinc-600',
};

const strengthStyles = {
  Strong: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  Medium: 'border-amber-200 bg-amber-50 text-amber-800',
  Early: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  Placeholder: 'border-zinc-200 bg-zinc-50 text-zinc-600',
};

const relatedLinks = [
  { to: '/internal', label: 'Internal dashboard' },
  { to: '/internal/digital-key-demo', label: 'Digital Key Demo' },
  { to: '/internal/fas-readiness', label: 'FAS Readiness' },
  { to: '/internal/bosa-evidence-pack', label: 'BOSA Evidence Pack' },
  { to: '/internal/digital-key-architecture', label: 'Digital Key Architecture' },
  { to: '/internal/identities', label: 'Identity Operations' },
  { to: '/internal/digital-key-accounts', label: 'Digital Key Accounts' },
  { to: '/internal/oidc-lab', label: 'OIDC Lab' },
  { to: '/internal/authenticator-preview', label: 'Authenticator Preview' },
  { to: '/internal/relying-party-profiles', label: 'Relying Party Profiles' },
  { to: '/internal/regulated-identifiers', label: 'Regulated Identifiers' },
  { to: '/internal/audit-governance-pack', label: 'Audit Governance Pack' },
];

function buildQuery(params) {
  const search = new URLSearchParams();
  if (params.identityId) search.set('identity_id', params.identityId);
  if (params.accountId) search.set('account_id', params.accountId);
  if (params.requestId) search.set('request_id', params.requestId);
  const query = search.toString();
  return query ? `?${query}` : '';
}

function Badge({ value, styles, fallback = 'border-zinc-200 bg-zinc-50 text-zinc-600' }) {
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${styles[value] || fallback}`}>{value}</span>;
}

function SummaryCard({ label, value, detail }) {
  return (
    <article className="border border-zinc-200 bg-white p-4">
      <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</p>
      <p className="mt-2 font-display text-2xl font-semibold tracking-tight text-zinc-900">{value}</p>
      <p className="mt-2 text-sm leading-6 text-zinc-600">{detail}</p>
    </article>
  );
}

function ReadinessSection({ section }) {
  return (
    <section className="border border-zinc-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{section.letter}</p>
          <h2 className="mt-1 font-display text-lg font-semibold">{section.title}</h2>
          <p className="mt-2 max-w-4xl text-sm text-zinc-600">{section.intro}</p>
        </div>
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-[78rem] text-sm">
          <thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="py-2 pr-4 font-medium">Readiness area</th>
              <th className="py-2 pr-4 font-medium">Current component</th>
              <th className="py-2 pr-4 font-medium">Internal link</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 pr-4 font-medium">Readiness strength</th>
              <th className="py-2 font-medium">Next action</th>
            </tr>
          </thead>
          <tbody>
            {section.items.map((item) => (
              <tr key={`${section.id}-${item.component}`} className="border-b border-zinc-100 align-top last:border-b-0">
                <td className="py-3 pr-4 font-medium text-zinc-800">{item.area}</td>
                <td className="py-3 pr-4 text-zinc-600">{item.component}</td>
                <td className="py-3 pr-4">
                  <Link to={item.linkTo} className="text-zinc-900 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-900">
                    {item.linkLabel}
                  </Link>
                </td>
                <td className="py-3 pr-4">
                  <Badge value={item.status} styles={statusStyles} />
                </td>
                <td className="py-3 pr-4">
                  <Badge value={item.strength} styles={strengthStyles} />
                </td>
                <td className="py-3 text-zinc-600">{item.nextAction}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SectionLinkBar() {
  return (
    <section className="mt-6 border border-zinc-200 bg-white p-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-lg font-semibold">Related internal references</h2>
          <p className="mt-1 text-sm text-zinc-600">These pages provide the internal context for the readiness tracker and remain platform-admin-only.</p>
        </div>
        <Link to="/internal/audit-governance-pack" className="text-sm text-zinc-900 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-900">
          Open audit governance
        </Link>
      </div>
      <div className="mt-4 flex flex-wrap gap-2 text-sm">
        {relatedLinks.map((link) => (
          <Link key={link.to} to={link.to} className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">
            {link.label}
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function PartnerTechnicalReadiness() {
  const [searchParams] = useSearchParams();
  const identityId = searchParams.get('identity_id') || '';
  const accountId = searchParams.get('account_id') || '';
  const requestId = searchParams.get('request_id') || '';

  const query = useMemo(() => buildQuery({ identityId, accountId, requestId }), [accountId, identityId, requestId]);
  const allItems = useMemo(() => readinessSections.flatMap((section) => section.items), []);
  const summary = useMemo(() => {
    const currentFoundation = allItems.filter((item) => ['Implemented', 'Internal lab'].includes(item.status)).length;
    const internalLabStage = allItems.filter((item) => item.status === 'Internal lab').length;
    const needsValidation = allItems.filter((item) => item.status.includes('validation')).length;
    const highPriorityGaps = allItems.filter(
      (item) =>
        item.risk === 'High' &&
        ['Draft', 'Not started', 'Needs validation', 'Needs partner validation', 'Needs BOSA/FAS validation', 'Out of scope for MVP'].includes(item.status),
    ).length;

    return { currentFoundation, internalLabStage, needsValidation, highPriorityGaps };
  }, [allItems]);

  return (
    <div data-testid="partner-technical-readiness-page">
      <VerificationBreadcrumb rootLabel="Internal" rootTo="/internal" current="Partner Technical Readiness" showDashboard={false} />

      <div className="mb-6">
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Internal admin</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Partner Technical Readiness v0.2</h1>
        <p className="mt-2 max-w-4xl text-sm text-zinc-600">
          Internal readiness dossier for future relying parties, BOSA/FAS discussions, technical partners, and potential pilots. It is a preparation tracker only, not a public integration guide and not an official readiness claim.
        </p>
      </div>

      <section className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="font-medium">Internal readiness tracker only</p>
        <p className="mt-1">
          This page is not a public partner onboarding guide, production OIDC/OAuth statement, official FAS/CSAM integration claim, certification claim, or LoA claim.
        </p>
      </section>

      <section className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard label="Current foundation" value={summary.currentFoundation} detail="Implemented and lab-stage capabilities already in the internal flow." />
        <SummaryCard label="Internal lab stage" value={summary.internalLabStage} detail="Items that are intentionally limited to the internal simulator and demo runner." />
        <SummaryCard label="Needs partner/BOSA/FAS validation" value={summary.needsValidation} detail="Items that still depend on external protocol, claims, or recognition guidance." />
        <SummaryCard label="High-priority gaps" value={summary.highPriorityGaps} detail="High-risk drafts, placeholders, and unresolved readiness questions." />
      </section>

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <h2 className="font-display text-lg font-semibold">Partner readiness summary</h2>
        <p className="mt-2 max-w-4xl text-sm text-zinc-600">
          The purpose of this tracker is to show what the internal platform can already demonstrate, what remains draft, and what must be validated before any partner discussion.
        </p>
        <div className="mt-4 flex flex-wrap gap-2 text-sm">
          <Link to="/internal/digital-key-demo" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Digital Key Demo</Link>
          <Link to="/internal/oidc-lab" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">OIDC Lab</Link>
          <Link to="/internal/authenticator-preview" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Authenticator Preview</Link>
          <Link to="/internal/relying-party-profiles" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Relying Party Profiles</Link>
          <Link to="/internal/fas-readiness" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">FAS Readiness</Link>
          <Link to="/internal/bosa-evidence-pack" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">BOSA Evidence Pack</Link>
          <Link to="/internal/audit-governance-pack" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Audit Governance Pack</Link>
          <Link to="/internal/digital-key-architecture" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Digital Key Architecture</Link>
          <Link to="/internal/regulated-identifiers" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Regulated Identifiers</Link>
        </div>
      </section>

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-semibold">How to read this page</h2>
            <p className="mt-2 max-w-4xl text-sm text-zinc-600">
              Each item links back to the internal page that demonstrates the current component. Status labels describe readiness only and do not imply approval or production readiness.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-zinc-600">
            <span className="border border-zinc-200 bg-zinc-50 px-2 py-1">Lab-only</span>
            <span className="border border-zinc-200 bg-zinc-50 px-2 py-1">Internal readiness</span>
            <span className="border border-zinc-200 bg-zinc-50 px-2 py-1">Safe claims</span>
            <span className="border border-zinc-200 bg-zinc-50 px-2 py-1">Validation pending</span>
          </div>
        </div>
      </section>

      {readinessSections.map((section) => (
        <div key={section.id} className="mt-6">
          <ReadinessSection section={section} />
        </div>
      ))}

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="font-display text-lg font-semibold">Internal flow notes</h2>
            <p className="mt-2 max-w-4xl text-sm text-zinc-600">
              Use this page together with the digital key demo, the audit pack, and the evidence pack to talk through the internal lab flow without drifting into public login or formal recognition claims.
            </p>
          </div>
          <Link to={`/internal/oidc-lab${query}`} className="text-sm text-zinc-900 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-900">
            Continue to OIDC Lab
          </Link>
        </div>
      </section>

      <SectionLinkBar />

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <h2 className="font-display text-lg font-semibold">What this page does not claim</h2>
        <ul className="mt-4 space-y-2 text-sm text-zinc-600">
          <li>No official certification, recognition, approval, or compliance statement.</li>
          <li>No official FAS/CSAM integration or LoA claim.</li>
          <li>No production OIDC/OAuth surface or public login product.</li>
          <li>No raw evidence, images, selfies, OCR/MRZ raw data, RRN/BIS, API keys, secrets, or tokens are exposed.</li>
        </ul>
      </section>
    </div>
  );
}
